import { useCallback, useRef } from "react";

interface ResumableTimeout {
  callback: () => void;
  duration: number;
  queuedAt: number;
  timeoutId: number;
}

export const useResumableTimeouts = () => {
  const timeoutsRef = useRef<Map<string, ResumableTimeout>>(new Map());

  const clearTimeout = useCallback((id: string) => {
    const existingTimeout = timeoutsRef.current.get(id);
    if (!existingTimeout) {
      return;
    }

    window.clearTimeout(existingTimeout.timeoutId);
    timeoutsRef.current.delete(id);
  }, []);

  const setTimeout = useCallback((id: string, callback: () => void, duration: number) => {
    const existingTimeout = timeoutsRef.current.get(id);
    if (existingTimeout) {
      window.clearTimeout(existingTimeout.timeoutId);
    }

    timeoutsRef.current.set(id, {
      callback,
      duration,
      queuedAt: Date.now(),
      timeoutId: window.setTimeout(() => {
        callback();
        timeoutsRef.current.delete(id);
      }, duration),
    });
  }, []);

  const pauseTimeouts = useCallback(() => {
    timeoutsRef.current.forEach((timeout) => {
      if (timeout.timeoutId === -1) {
        return;
      }

      window.clearTimeout(timeout.timeoutId);

      const timeElapsed = Date.now() - timeout.queuedAt;
      const timeRemaining = timeout.duration - timeElapsed;

      timeout.timeoutId = -1;
      timeout.duration = timeRemaining;
    });
  }, []);

  const resumeTimeouts = useCallback(() => {
    timeoutsRef.current.forEach((timeout, id) => {
      if (timeout.timeoutId !== -1) {
        return;
      }

      timeout.queuedAt = Date.now();
      timeout.timeoutId = window.setTimeout(() => {
        timeout.callback();
        timeoutsRef.current.delete(id);
      }, timeout.duration);
    });
  }, []);

  return { clearTimeout, setTimeout, pauseTimeouts, resumeTimeouts };
};
